import { StateCreator } from 'zustand';

import { Conflict } from '@/types/Conflicten';
import { Defect } from '@/types/Defecten';

import { Reserve } from '../types';
import { DDPNotitie, DDPStatus } from '../types/ddpTypes';
import {
  createEmptyNotitie,
  getRelatedNotitieFields,
  getSublabel,
  sidepanelTabs,
  SidepanelViews,
  Tabs,
} from '../utils/sidepanel';

import { PlankDataStore } from './types/PlankDataStore';
import { SidepanelAttributes, SidepanelStore } from './types/SidepanelStore';

const defaultSidepanel = (): SidepanelAttributes =>
  structuredClone({
    show: false,
    tab: 'Notitie' as Tabs,
    view: undefined,
    voorraad: undefined,
    voorraadRegionaal: undefined,
    sublabel: '',
    eenheid: undefined,
    defect: undefined,
    isOnderhoud: false,
    defects: [],
    conflict: undefined,
    reserve: undefined,
    isBusy: false,
    isAccepteerDialogOpen: false,
    notitie: createEmptyNotitie(),
  });
/**
 * Zustand store for DDP sidepanel.
 * View Conflict details:
 * - load conflict, get related defects
 *
 * View Materieel details:
 * - load defect, get related conflicts
 *
 *
 *  About related data: its easiest to match conflict / defect data where it has been set.
 */
const sidepanelStateCreator: StateCreator<SidepanelStore & Partial<PlankDataStore>> = (set, get, __) => ({
  sidepanel: defaultSidepanel(),
  updateNotitieStatus: (status: DDPStatus) => {
    set((state) => ({ sidepanel: { ...state.sidepanel, notitie: { ...state.sidepanel.notitie, status } } }));
  },
  updateNotitieOpmerking: (opmerking: string) => {
    set((state) => ({ sidepanel: { ...state.sidepanel, notitie: { ...state.sidepanel.notitie, opmerking } } }));
  },
  updateIsBusy: (flag: boolean) => {
    set((state) => ({ sidepanel: { ...state.sidepanel, isBusy: flag } }));
  },
  getTabs: () => (sidepanelTabs?.[get().sidepanel.view as keyof typeof sidepanelTabs] as Tabs[]) ?? [],
  setTab(tab) {
    set((state) => ({ sidepanel: { ...state.sidepanel, tab } }));
  },
  isEenheidSelected: (args) => {
    const store = get();
    return store.sidepanel.show && store.isSameView(args);
  },

  isVoorraadSelected({ voorraad = undefined, voorraadRegionaal = undefined }) {
    const { show, voorraad: currentVoorraad, voorraadRegionaal: currentRegionaal } = get().sidepanel;
    return (
      show &&
      Boolean(
        (currentVoorraad &&
          voorraad &&
          currentVoorraad.rbc === voorraad.rbc &&
          currentVoorraad.materieel === voorraad.materieel) ||
        (currentRegionaal &&
          voorraadRegionaal &&
          currentRegionaal.knoop === voorraadRegionaal.knoop &&
          currentRegionaal.materieel === voorraadRegionaal.materieel),
      )
    );
  },
  /** Open the sidepanel and provide necessary.
   *
   * @param eenheidProps = { eenheid, conflict, defect, reserve, notitie}
   */
  setEenheid: async ({
    eenheid,
    conflict = undefined,
    defect = undefined,
    reserve = undefined,
    notitie = undefined,
    isOnderhoud = undefined,
  }) => {
    // Close panel first to visually reload.
    const currentShow = get().sidepanel.show;
    const isSameView = get().isSameView({ eenheid, conflict, defect, reserve, isOnderhoud });
    const setState = () => {
      const [view, viewData]: [SidepanelViews, Defect | Conflict | Reserve | undefined] = (conflict && [
        'conflict',
        conflict,
      ]) ??
        (defect && ['defect', defect]) ??
        (reserve && ['reserve', reserve]) ?? [undefined, undefined];
      const tab = (sidepanelTabs?.[view as keyof typeof sidepanelTabs]?.[0] as Tabs) ?? 'Notitie';
      const sublabel = getSublabel?.[String(conflict?.type ?? defect?.soort ?? 'RESERVE')];
      const relatedNotitieFields = getRelatedNotitieFields(view, viewData) ?? createEmptyNotitie();
      const newNotitie: DDPNotitie = { ...relatedNotitieFields, status: undefined, opmerking: undefined };
      set((state) => {
        return {
          sidepanel: {
            ...state.sidepanel,
            show: true,
            view,
            eenheid,
            conflict,
            defect,
            isOnderhoud,
            sublabel,
            tab,
            reserve,
            notitie: notitie !== undefined ? { ...notitie } : { ...newNotitie },
          },
        };
      });
    };
    if (currentShow) {
      get().closeSidepanel();
      if (!isSameView) setTimeout(setState, 200);
    } else {
      setState();
    }
  },
  async setSidepanelVoorraad(voorraad) {
    const currentShow = get().sidepanel.show;
    const setState = () =>
      set((state) => ({
        sidepanel: {
          ...state.sidepanel,
          show: true,
          tab: 'Voorraad',
          voorraad,
        },
      }));
    if (currentShow) {
      get().closeSidepanel();
      setTimeout(setState, 200);
    } else {
      setState();
    }
  },
  async setSidepanelVoorraadRegionaal(voorraadRegionaal) {
    const currentShow = get().sidepanel.show;
    const setState = () =>
      set((state) => ({
        sidepanel: {
          ...state.sidepanel,
          show: true,
          tab: 'VoorraadRegionaal',
          voorraadRegionaal,
        },
      }));
    if (currentShow) {
      get().closeSidepanel();
      setTimeout(setState, 200);
    } else {
      setState();
    }
  },
  isSameView({ eenheid, conflict = undefined, defect = undefined, reserve = undefined, isOnderhoud = undefined }) {
    const {
      eenheid: sidepanelEenheid,
      view,
      defect: sidepanelDefect,
      reserve: sidepanelReserve,
      isOnderhoud: sidepanelIsOnderhoud,
    } = get().sidepanel;
    return (
      sidepanelEenheid === eenheid &&
      ((view === 'defect' &&
        defect?.id !== undefined &&
        defect.id === sidepanelDefect?.id &&
        Boolean(isOnderhoud) === Boolean(sidepanelIsOnderhoud)) ||
        (view === 'conflict' && conflict !== undefined) ||
        (view === 'reserve' && reserve?.id !== undefined && reserve.id === sidepanelReserve?.id))
    );
  },
  setIsAccepteerDialogOpen: (open: boolean) => {
    set((state) => ({ sidepanel: { ...state.sidepanel, isAccepteerDialogOpen: open } }));
  },
  closeSidepanel() {
    set({
      sidepanel: defaultSidepanel(),
    });
  },
  resetNotities: () => set((state) => ({ sidepanel: { ...state.sidepanel, notitie: { ...createEmptyNotitie() } } })),
});
export default sidepanelStateCreator;
